'use client';
import { useCallback } from 'react';
import { useTranslation } from '@/app/hooks/useTranslation';
import { useRouter, usePathname } from 'next/navigation';
import { useUser } from '@/app/hooks/useUser';
import utilsService from '@/app/helpers/utilsService';
import { useGlobalDialogV2 } from '@/app/contexts/GlobalDialogV2';
import { myRoomService } from '@/app/service/myRoomService';
import { currentMr, withMr } from './mrEnv';
import { rememberReturnPath } from './returnPath';
import { canEnterRoom, ROOM_UPDATE_NOTICE } from './appCompat';

// 마이룸 진입 규칙 한 곳. 홈 배너 · 퀵메뉴(3D전시관) · 마이채널 카드가 같은 분기를 쓴다.
//
//   연동됨   -> /myroom            (본인 방)
//   미연동   -> /myroom?guest=1    (쇼케이스 샘플 방 — 여기서 마이룸 가이드가 뜬다)
//   비로그인 -> /login             (마이룸 API 가 전부 인증 필요라 방을 띄울 수 없다)
//
// 연동 여부는 클릭 시점에 조회한다. 배너·퀵메뉴는 렌더에 이 값이 필요 없어서,
// 미리 불러두면 홈에 불필요한 요청만 늘어난다.
export function useMyRoomEntry() {
    const router = useRouter();
    const pathname = usePathname();
    const { user } = useUser();
    const { showDialog, hideDialog } = useGlobalDialogV2();
    const { t } = useTranslation();

    return useCallback(async () => {
        // 구버전 iOS 앱은 방 iframe 이 앱 밖으로 튕겨 세션 주입부터 깨진다.
        // 들여보내는 대신 업데이트를 안내한다(appCompat 참고).
        if (!canEnterRoom()) {
            showDialog({
                title: t('myroom_misc_update_title', ROOM_UPDATE_NOTICE.title),
                message: t('myroom_misc_update_message', ROOM_UPDATE_NOTICE.message),
                acceptText: t('confirm', '확인'),
                showAccept: true,
                onAccept: hideDialog,
            });
            return;
        }
        if (!user?.id) {
            router.push(utilsService.buildLoginUrl(pathname));
            return;
        }
        // 방에서 ✕ 로 나올 때 여기로 돌아온다.
        rememberReturnPath();
        let linked = false;
        let onboarding = false;
        try {
            const st: any = await myRoomService.status(currentMr());
            linked = !!st?.linked;
            // 마이룸 계정은 있는데 가입을 안 끝낸 회원. 게스트 샘플 방으로 보내면
            // 처음부터 다시 시작하게 되므로, 연동 화면으로 보내 이어서 마치게 한다.
            onboarding = st?.myroom_status === 'onboarding_required';
        } catch {
            // 조회 실패면 샘플 방으로. 본인 방을 못 여는 것보다 낫다.
        }
        router.push(withMr(linked || onboarding ? '/myroom' : '/myroom?guest=1'));
    }, [router, user?.id, showDialog, hideDialog, t]);
}
